import { useEffect, lazy, Suspense, ComponentType } from 'react';
import {
  App as AppLayout,
  LoadingIndicator,
  EmptyState,
  EmptyStateTitle,
  EmptyStateButton,
  IconBanActive,
  EmptyStateText,
  IconExclamationAltActive,
  spacer16,
  spacer24,
  spacer48,
  screenXsMax,
  screenSmMax,
} from '@spotify-internal/encore-web';
import { Redirect, Route, Switch, RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router';
import * as Sentry from '@sentry/browser';
import styled from 'styled-components';
import { currentTermsAndConditions, useAnnouncements } from 'libs/services/announcements';
import {
  AUTH_CALLBACK_PATH,
  handleAuthCodeResponse,
  logoutUser,
  SITE_ROOT_URL,
} from 'libs/services/auth';
import { MediaPlaybackProvider } from 'components/MediaPlayback';
import gTagManager from 'libs/services/logging';
import PageLoadingIndicator from 'components/PageLoadingIndicator';
import Banner from 'components/Banner';
import StickyTriggerProvider from 'components/StickyTrigger';
import ErrorBoundary from 'components/ErrorBoundary';
import RemoteConfig from './RemoteConfig/RemoteConfig';
import SkipLink from '../components/SkipLink';
import { ForbiddenError, NotFoundError } from 'libs/services/request';
import { SnackbarProvider, SnackbarContainer } from './Snackbar';
import { NavigationHeader, SidePanel, SidePanelState } from './Navigation';
import { isSongwriterTeamUser } from 'libs/utils';
import Footer from './Footer';
import { UserEntity } from '../libs/services/s4pTypes';
import { WithUser } from './WithUser';
import { useT } from '@spotify-internal/i18n-core';

const AcceptInvite = lazy(
  () => import(/* webpackChunkName: "AcceptInvite" */ './ManageTeam/AcceptInvite'),
);
const AllWrittenByPlaylists = lazy(
  () => import(/* webpackChunkName: "AllWrittenByPlaylists" */ './AllWrittenByPlaylists'),
);
const Catalog = lazy(() => import(/* webpackChunkName: "Catalog" */ './Catalog/CatalogContainer'));
const FAQ = lazy(() => import(/* webpackChunkName: "FAQ" */ './FAQ'));
const ManageTeam = lazy(
  () => import(/* webpackChunkName: "ManageTeam" */ './ManageTeam/ManageTeam'),
);
const Recording = lazy(
  () => import(/* webpackChunkName: "Recording" */ './Recording/RecordingContainer'),
);
const ReviewMatches = lazy(
  () => import(/* webpackChunkName: "ReviewMatches" */ './Catalog/Suggestions/ReviewMatches'),
);
const SearchAll = lazy(
  () => import(/* webpackChunkName: "SearchAll" */ './Search/SearchAllContainer'),
);
const SearchEntity = lazy(
  () => import(/* webpackChunkName: "SearchEntity" */ './Search/SearchEntity'),
);
const Songwriters = lazy(() => import(/* webpackChunkName: "Songwriters" */ './Songwriters'));
const TermsAndConditions = lazy(
  () => import(/* webpackChunkName: "TermsAndConditions" */ 'components/TermsAndConditions'),
);
const SongwriterFirstTimeUser = lazy(
  () =>
    import(
      /* webpackChunkName: "SongwriterFirstTimeUser" */ 'components/TermsAndConditions/SongwriterFirstTimeUser'
    ),
);
const WelcomeGuide = lazy(() => import(/* webpackChunkName: "WelcomeGuide" */ './WelcomeGuide'));
const Work = lazy(() => import(/* webpackChunkName: "Work" */ './Work/WorkContainer'));
const Writer = lazy(
  () => import(/* webpackChunkName: "Writer" */ './Catalog/WriterContainer/WriterContainer'),
);
const WriterCuration = lazy(
  () => import(/* webpackChunkName: "WriterCuration" */ './Catalog/WriterCuration'),
);

const StyledAppLayout = styled(AppLayout)`
  /* overrides default padding applied by Encore to "Content" element */
  & > div:nth-child(3) {
    padding-inline-start: 0;
    padding-inline-end: 0;
    max-inline-size: none;
  }
`;

const InnerContent = styled.div`
  @media (max-width: ${screenXsMax}) {
    padding: 0 ${spacer16} ${spacer24} ${spacer16};
  }

  @media (min-width: ${screenXsMax}) and (max-width: ${screenSmMax}) {
    padding: 0 ${spacer24} ${spacer24} ${spacer24};
  }

  padding: 0 ${spacer48} ${spacer24} ${spacer48};
`;

const CenterAlign = styled.div`
  min-height: calc(100vh - 65px);
  display: grid;
  place-content: center;
`;

const WithRemoteConfig = (Component: ComponentType<any>) => (props: any & { user: UserEntity }) => {
  return (
    <RemoteConfig username={props.user.username}>
      <Component {...props} />
    </RemoteConfig>
  );
};

function LoggedIn(props: { user: UserEntity } & RouteComponentProps) {
  const announcements = useAnnouncements();
  const { user } = props;

  const creatorId = user.team.resources
    .find((resource) => resource.startsWith('spotify:songwriter'))
    ?.split(':')[2];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [props.location.pathname]);

  useEffect(() => {
    const gaPartnerUserId = user.partnerUserIds.find(
      (partnerUserId) => partnerUserId.partner === 'googleanalytics',
    );

    const userOrgType = isSongwriterTeamUser(user.team.organizationUri)
      ? 'songwriterOrg'
      : 'publisherName';

    gTagManager({
      event: 'logIn',
      userId: gaPartnerUserId ? gaPartnerUserId.id : 'unknown',
      isEmployee: user.employee,
      songwriterOrg: null,
      publisherName: null,
      [userOrgType]: user.team.organizationName, // overwrites either the songwriterOrg or publisherName with a not null value.
    });

    Sentry.setContext('publisher', {
      organizationName: user.team.organizationName,
    });
  }, [user]);

  if (!announcements.accepted.includes(currentTermsAndConditions)) {
    return isSongwriterTeamUser(user.team.organizationUri) ? (
      <SongwriterFirstTimeUser onAccepted={announcements.acceptAnnouncement} user={user} />
    ) : (
      <TermsAndConditions
        acceptedAnnouncements={announcements.accepted}
        onAccepted={announcements.acceptAnnouncement}
        user={user}
      />
    );
  }

  // Main application content
  let content = (
    <Switch>
      <Route exact path="/match-data" component={ReviewMatches} />
      <Route
        path="/"
        render={(props) => (
          <Banner>
            {(banner) => (
              <>
                <SkipLink href="#innerContent" />
                <StyledAppLayout
                  sidebarWidth="0px"
                  content={
                    <ErrorBoundary
                      resetKey={props.location.pathname}
                      onError={(err, errInfo) => {
                        if (err instanceof NotFoundError || err instanceof ForbiddenError) return;
                        Sentry.withScope((scope) => {
                          scope.setExtra('info', errInfo);
                          scope.setLevel('fatal');
                          Sentry.captureException(err);
                        });
                      }}
                      fallback={renderPageError}
                    >
                      <NavigationHeader user={user} {...props} />
                      <SidePanel />
                      {banner}
                      <InnerContent id="innerContent">
                        <Suspense
                          fallback={
                            <CenterAlign>
                              <LoadingIndicator indicatorSize="md" />
                            </CenterAlign>
                          }
                        >
                          <Route
                            exact
                            path="/"
                            render={() => {
                              if (isSongwriterTeamUser(user.team.organizationUri)) {
                                return <Redirect to={`/creator/${creatorId}/curation`} />;
                              } else {
                                return <Redirect to="/works" />;
                              }
                            }}
                          />
                          <Route exact path="/search" component={SearchAll} />
                          <Route exact path="/search/:entityType/:query" component={SearchEntity} />
                          <Route exact path="/works" component={Catalog} />
                          <Route exact path="/writers" component={Songwriters} />
                          <Route exact path="/all-written-by-playlists" component={AllWrittenByPlaylists} />
                          <Route
                            exact
                            path="/work/:workGid/recording/:recordingGid"
                            component={Recording}
                          />
                          <Route exact path="/work/:workGid" component={Work} />
                          <Route path="/writer/:ipi" component={Writer} />
                          <Route
                            path="/creator/:creatorId/curation"
                            render={(routeProps) => (
                              <WriterCuration userType="songwriter" {...routeProps} />
                            )}
                          />
                          <Route
                            path="/manage-team"
                            render={(routeProps) => {
                              return user.permissions.includes('team.view') ? (
                                <ManageTeam {...routeProps} />
                              ) : (
                                <Redirect to="/" />
                              );
                            }}
                          />
                          <Route exact path="/welcome-guide" component={WelcomeGuide} />
                          <Route exact path="/faq" component={FAQ} />
                        </Suspense>
                        <SnackbarContainer />
                      </InnerContent>
                    </ErrorBoundary>
                  }
                  footer={<Footer user={user} />}
                />
              </>
            )}
          </Banner>
        )}
      />
    </Switch>
  );

  // Application context providers.
  content = <MediaPlaybackProvider>{content}</MediaPlaybackProvider>;
  content = <StickyTriggerProvider>{content}</StickyTriggerProvider>;
  content = <SnackbarProvider>{content}</SnackbarProvider>;
  content = <SidePanelState>{content}</SidePanelState>;

  return content;
}

const LoggedInWithRouter = WithUser(WithRemoteConfig(withRouter(LoggedIn)));

export default function App() {
  return (
    <ErrorBoundary
      onError={(err, errInfo) => {
        if (err instanceof NotFoundError || err instanceof ForbiddenError) return;
        Sentry.withScope((scope) => {
          scope.setExtra('info', errInfo);
          scope.setLevel('fatal');
          Sentry.captureException(err);
        });
      }}
      fallback={renderAppError}
    >
      <Suspense
        fallback={
          <CenterAlign>
            <LoadingIndicator indicatorSize="md" />
          </CenterAlign>
        }
      >
        <Switch>
          <Route
            exact
            path={AUTH_CALLBACK_PATH}
            render={(props) => {
              handleAuthCodeResponse(props.location.search);
              return <PageLoadingIndicator />;
            }}
          />
          <Route
            exact
            path="/logout"
            render={() => {
              logoutUser();
              window.location.assign(
                `https://accounts.spotify.com/logout?continue=${encodeURIComponent(SITE_ROOT_URL)}`,
              );
              return <PageLoadingIndicator />;
            }}
          />
          <Route exact path="/accept-invite/:token" component={AcceptInvite} />
          <Route component={LoggedInWithRouter} />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

function renderAppError(err: Error, t: ReturnType<typeof useT>) {
  if (err instanceof ForbiddenError) {
    return (
      <EmptyState
        style={{ position: 'absolute', width: '100%', height: '100%' }}
        variant="fullscreen"
        icon={IconBanActive}
        iconColorSet="announcement"
      >
        <EmptyStateTitle>
          {t(
            'ESTITLE_APPERROR_0',
            "You don't have access to this page",
            'Title for message that shows when a user does not have access to a page',
          )}
        </EmptyStateTitle>
        <EmptyStateText>
          {t(
            'ESTEXT_APPERROR_0',
            'Make sure you are signed into the correct organization or reach out to the administrator for the account.',
            'Message that shows when a user does not have access to a page',
          )}
        </EmptyStateText>
        <EmptyStateButton
          target="_blank"
          rel="noopener noreferrer"
          href="https://noteable.spotify.com/pages/publishing-analytics"
        >
          {t(
            'ESBUTTON_APPERROR_0',
            'Learn more',
            'Button that a user clicks to learn more about why they do not have access to a page',
          )}
        </EmptyStateButton>
      </EmptyState>
    );
  }

  return (
    <EmptyState
      style={{ position: 'absolute', width: '100%', height: '100%' }}
      variant="fullscreen"
    >
      <IconExclamationAltActive semanticColor="textNegative" iconSize={48} />
      <EmptyStateTitle>
        {t(
          'ESTITLE_APPERROR_1',
          "Couldn't load results",
          'Title for message that shows when the app is not loading',
        )}
      </EmptyStateTitle>
      <EmptyStateText>
        {t(
          'ESTEXT_APPERROR_1',
          'Wait a few minutes, then try again.',
          'Message that shows when the app is not loading',
        )}
      </EmptyStateText>
    </EmptyState>
  );
}

function renderPageError(err: Error, t: ReturnType<typeof useT>) {
  if (err instanceof ForbiddenError) {
    return (
      <EmptyState
        variant="fullscreen"
        style={{ marginTop: '275px' }}
        icon={IconBanActive}
        iconColorSet="announcement"
      >
        <EmptyStateTitle>
          {t(
            'ESTITLE_APPERROR_2',
            'No permission',
            'Title for message that shows when a user does not have permission to access a page',
          )}
        </EmptyStateTitle>
        <EmptyStateText>
          {t(
            'ESTEXT_APPERROR_2',
            'Make sure you are signed into the correct organization or reach out to the administrator for the account.',
            'Message that shows when a user does not have permission to access a page',
          )}
        </EmptyStateText>
      </EmptyState>
    );
  }

  return (
    <EmptyState variant="fullscreen" style={{ marginTop: '275px' }}>
      <IconExclamationAltActive semanticColor="textNegative" iconSize={48} />
      <EmptyStateTitle>
        {t(
          'ESTITLE_APPERROR_3',
          "Couldn't load results",
          'Title for message that shows when the app is not loading',
        )}
      </EmptyStateTitle>
      <EmptyStateText>
        {t(
          'ESTEXT_APPERROR_3',
          'Wait a few minutes, then try again.',
          'Message that shows when the app is not loading',
        )}
      </EmptyStateText>
    </EmptyState>
  );
}
